import last from 'lodash/fp/last'
import toString from 'lodash/fp/toString'
import toInteger from 'lodash/fp/toInteger'
import toArray from 'lodash/fp/toArray'
import map from 'lodash/fp/map'
import reduce from 'lodash/fp/reduce'
import compose from 'lodash/fp/compose'
import isString from 'lodash/fp/isString'

/**
 * Password regex validator
 *
 * Longer than 8 characters and conforms 3 of this conditions
 * - Has a number
 * - Has lower letter
 * - Has upper letter
 * - Has special character
 */
export const PASSWORD_REGEX = /^((?=\S{8,})((?=.*\d)(?=.*[a-z])(?=.*[A-Z])|(?=.*\d)(?=.*[a-zA-Z])(?=.*[\W_])|(?=.*[a-z])(?=.*[A-Z])(?=.*[\W_]))\S*)$/

/**
 * Spanish zip code regex validator
 *
 * number between 4 and 5 digits for zero prefixed
 */
export const ZIP_CODE_REGEX = /^\d{5}$/

/**
 * Phone number regex validator
 * see in triodos.es contact form
 */
export const PHONE_NUMBER_REGEX = /^([0-9()+-]+[ ])*[0-9()+-]+$/

/**
 * helper to NIF, NIE, CIF validators that transform
 * to uppercase and remove dashes
 *
 * @param {any} value - value to normalize
 * @return {any} nomralized value or value if is not string
 */
export const normalizeNID = value => isString(value) ?
  value.toUpperCase().replace(/[\s-]+/g, '') :
  value

/**
 * Spanish company CIF validator
 * @see {@link https://es.wikipedia.org/wiki/N%C3%BAmero_de_identificaci%C3%B3n_fiscal}
 */

/** @const {Regexp} CIF_REGEX cif matcher (letter)(digit)(control) */
export const CIF_REGEX = /^([ABCDEFGHJKLMNPQRSUVW])(\d{7})([0-9A-J])$/

/** @const {String} CIF_CONTROL_LETTERS cif control letters */
export const CIF_CONTROL_LETTERS = 'JABCDEFGHI'

/**
 * sum the CIF digits by spanish algorithm
 * - odd index: mul by 2 and summing their digits. Ex: 6 => 6 * 2 = 12 -> 1 + 2 = 3
 * - even index: sum the number
 * @sig [Number] -> Number
 */
export const sumCIFNumber = reduce.convert({ cap: false })((sum, n, index) => {
  let digit = n
  if (index % 2 === 0) {
    digit *= 2
    if (digit >= 10) digit -= 9
  }
  return sum + digit
}, 0)

/**
 *
 *
 * @sig Number -> Number -> Number
 * @param {Number} minuend - minuend
 * @param {Number} n - subtrahend
 * @return {Number} subtract base - n or 0 if n is 0
 */
export const subtractIfNoZero = minuend => subtrahend => subtrahend !== 0 ?
  minuend - subtrahend :
  subtrahend

/**
 * get the CIF control digit
 * see inline comments to see the transformation
 *
 * @param {String} cifNumber
 * @return {Number} control digit
 */
export const getCIFControlDigit = compose(
  subtractIfNoZero(10), // substract to 10 if last digit is not zero. Ex: 10 - 3
  toInteger, // again to integer. Ex: 3
  last, // get last digit. Ex: '3'
  toString, // converts to string. Ex: º'23'
  sumCIFNumber, // sum number with spanish algorithm. Ex: 23
  map(toInteger), // converts items in array to integer to sum. Ex: [1, 2, ...]
  toArray // converts string of number on array o string numbers. Ex: ['1', '2', ...]
)

/**
 * validate spanish company id
 *
 * @param {String} cif - to validate
 * @return {Boolean} is valid
 */
export const validateCIF = cif => {
  const match = normalizeNID(cif).match(CIF_REGEX)

  if (!match) return false

  // eslint-disable-next-line no-unused-vars
  const [_fullMatch, letter, number, control] = match

  const controlDigit = getCIFControlDigit(number)
  const controlLetter = CIF_CONTROL_LETTERS.charAt(controlDigit)

  if (/[ABEH]/.test(letter)) return control === controlDigit.toString()
  if (/[NPQRSW]/.test(letter)) return control === controlLetter

  return control === controlDigit.toString() || control === controlLetter
}

/**
 * Spanish NIF NIE validator
 * @see {@link https://es.wikipedia.org/wiki/N%C3%BAmero_de_identificaci%C3%B3n_fiscal}
 */

/** @const {Regexp} NIF_REGEX nif matcher (digit)(control) */
export const NIF_REGEX = /^(\d{8})([TRWAGMYFPDXBNJZSQVHLCKET])$/

/** @const {Sting} NIF_CONTROL_LETTERS nif control letters */
export const NIF_CONTROL_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKET'

/**
 * validates spanish nif
 *
 * @param {String} nifNie - value of nif or nie to validate
 * @return {Boolean} is valid cif or nie
 */
export const validateNIFNIE = nifNie => {
  const match = normalizeNID(nifNie)
    // replace nie initial letters with correspond number
    .replace(/^X/, '0')
    .replace(/^Y/, '1')
    .replace(/^Z/, '2')
    // match with regex
    .match(NIF_REGEX)

  if (!match) return false

  // eslint-disable-next-line no-unused-vars
  const [_fullMatch, digits, letter] = match
  const controlLetter = NIF_CONTROL_LETTERS.charAt(parseInt(digits) % 23)

  return controlLetter === letter
}

const URL_PATTERN = new RegExp('^(https?:\\/\\/)?' + // protocol
  '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.?)+[a-z]{2,}|' + // domain name
  '((\\d{1,3}\\.){3}\\d{1,3}))' + // ip (v4) address
  '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port
  '(\\?[;&amp;a-z\\d%_.~+=-]*)?' + // query string
  '(\\#[-a-z\\d_]*)?$', 'i')
/**
 * Validates URL
 * @param {String} url
 * @returns {Boolean}
 */
export const validateUrl = url => {
  return URL_PATTERN.test(url)
}
